/**
 * Phase 58.1 — Cross-deal period-grouping helpers for the account Plan
 * Schedule + combined receipts (COMBINE-01/03/04).
 *
 * Wave 0 (58.1-01) lands these as REAL exported, typed signatures with
 * bodies that throw — nothing imports them yet, so nothing breaks. Plan
 * 58.1-04 fills the bodies and wires them into `account-detail.tsx`,
 * replacing the `${quoteGroupId}#${periodIndex}` grouping key at
 * `account-detail.tsx:2436`.
 *
 * See: .planning/phases/58.1-billing-anchor-day-on-add-plan-with-prorated-first-period-and-cross-deal-combined-receipt-by-period-start/58.1-CONTEXT.md §Combined receipt
 */

export interface GroupablePeriod {
  id: number;
  membershipId: number;
  periodIndex: number;
  /** YYYY-MM-DD */
  periodStart: string;
  quoteGroupId?: string | null;
}

export interface PeriodReceiptGroup<T extends GroupablePeriod> {
  /** Canonical period start — the group key. */
  periodStart: string;
  /** The one quoteGroupId every member shares, else null (cross-deal or solo). */
  quoteGroupId: string | null;
  members: T[];
}

/**
 * COMBINE-01/03. Group an account's periods into receipt rows.
 *
 * Algorithm (locked decision, see 58.1-04-PLAN §decision — implement
 * verbatim, do not re-derive):
 *   1. `dealMin`: for every period with a non-null `quoteGroupId`, key
 *      `${quoteGroupId}#${periodIndex}` -> the MIN `periodStart` among
 *      periods sharing that key.
 *   2. `canonicalStart(p) = p.quoteGroupId ? dealMin.get(key)! : p.periodStart`.
 *   3. Group by `canonicalStart`; the group's `periodStart` IS that
 *      canonical value.
 *   4. `quoteGroupId` = the common non-null value when every member shares
 *      one, else `null`.
 *   5. Sort groups by `min(member.membershipId)` then `periodStart`
 *      (preserves today's order).
 *
 * Why not a plain `periodStart` key: a deal whose lines have staggered
 * start dates is ONE row today (`account-detail.tsx:2427-2430` says so
 * explicitly) and a plain key would split it, regressing
 * `feedback_plan_deal_single_line`. Why not union-find over both
 * relations: it chains, and can merge cycle 0 with cycle 1 of the same
 * recurring deal. Canonicalisation is provably monotone (never splits a
 * currently-merged set) AND non-chaining.
 */
export function groupPeriodsForReceipts<T extends GroupablePeriod>(
  periods: readonly T[],
): PeriodReceiptGroup<T>[] {
  // 1. dealMin: for every period with a non-null quoteGroupId, key
  //    `${quoteGroupId}#${periodIndex}` -> the MIN periodStart among periods
  //    sharing that key (a deal's own cycle, canonicalised to its earliest
  //    constituent line's start).
  const dealMin = new Map<string, string>();
  for (const p of periods) {
    if (p.quoteGroupId == null) continue;
    const key = `${p.quoteGroupId}#${p.periodIndex}`;
    const existing = dealMin.get(key);
    if (existing === undefined || p.periodStart < existing) {
      dealMin.set(key, p.periodStart);
    }
  }

  // 2. canonicalStart(p) = p.quoteGroupId ? dealMin.get(key)! : p.periodStart
  const canonicalStart = (p: T): string => {
    if (p.quoteGroupId == null) return p.periodStart;
    const key = `${p.quoteGroupId}#${p.periodIndex}`;
    return dealMin.get(key) ?? p.periodStart;
  };

  // 3. Group by canonicalStart; the group's periodStart IS that canonical value.
  const byCanonicalStart = new Map<string, T[]>();
  for (const p of periods) {
    const key = canonicalStart(p);
    const bucket = byCanonicalStart.get(key);
    if (bucket) bucket.push(p);
    else byCanonicalStart.set(key, [p]);
  }

  // 4. quoteGroupId = the single common non-null value when every member
  //    shares one, else null (cross-deal or all-legacy/solo).
  const groups: PeriodReceiptGroup<T>[] = [];
  for (const [periodStart, members] of byCanonicalStart) {
    const uniqueGroupIds = new Set(members.map((m) => m.quoteGroupId ?? null));
    const quoteGroupId = uniqueGroupIds.size === 1 ? [...uniqueGroupIds][0]! : null;
    groups.push({ periodStart, quoteGroupId, members });
  }

  // 5. Sort by min(member.membershipId) then periodStart (preserves today's order).
  groups.sort((a, b) => {
    const aMin = Math.min(...a.members.map((m) => m.membershipId));
    const bMin = Math.min(...b.members.map((m) => m.membershipId));
    if (aMin !== bMin) return aMin - bMin;
    return a.periodStart.localeCompare(b.periodStart);
  });

  return groups;
}

/**
 * COMBINE-04. Row label: "Plan #12" for a single deal, "Plan #12 + Plan #47"
 * when the row genuinely spans deals. Never fabricates an id.
 */
export function groupPlanLabel<T extends GroupablePeriod>(members: readonly T[]): string {
  // Partition members by deal identity: quoteGroupId, or their own
  // membershipId when they carry no quoteGroupId (a solo/legacy membership
  // is its own deal for labelling purposes).
  const minMembershipIdByDeal = new Map<string, number>();
  for (const m of members) {
    const dealKey = m.quoteGroupId ?? `solo-${m.membershipId}`;
    const existing = minMembershipIdByDeal.get(dealKey);
    if (existing === undefined || m.membershipId < existing) {
      minMembershipIdByDeal.set(dealKey, m.membershipId);
    }
  }
  const ids = [...minMembershipIdByDeal.values()].sort((a, b) => a - b);
  return ids.map((id) => `Plan #${id}`).join(" + ");
}
